/* ============================================================================
 * EditableText — click-to-edit text for the builder's PREVIEW.
 *
 * The preview is where the contractor actually reads the proposal, so words
 * get fixed here in place instead of round-tripping through the builder.
 * Click (or focus + Enter) swaps the text for an inline input/textarea that
 * inherits the surrounding typography; blur or Enter commits (Ctrl/Cmd+Enter
 * for multiline), Escape cancels. Commits go through the SAME builder state
 * helpers manual edits use, so dirty-tracking, Save, and autosave all apply.
 *
 * SAFETY: everything is gated on `editable`. The client portal and print
 * renders never pass it, so no edit affordance can ever reach a client.
 * ========================================================================== */
import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

export const EditableText = ({
  editable = false,
  value = '',
  onCommit,
  multiline = true,
  placeholder = 'Click to add…',
  className = '',
  as: Tag = 'span',
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const fieldRef = useRef(null);

  useEffect(() => {
    if (!editing) return;
    const el = fieldRef.current;
    if (!el) return;
    el.focus();
    // Caret at the end beats select-all for "fix one word" edits.
    const len = el.value.length;
    try {
      el.setSelectionRange(len, len);
    } catch (_) {
      /* not all input types support it */
    }
    if (el.tagName === 'TEXTAREA') {
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    }
  }, [editing]);

  if (!editable) {
    return value ? <Tag className={className}>{value}</Tag> : null;
  }

  const startEditing = () => {
    setDraft(value || '');
    setEditing(true);
  };

  const commit = () => {
    setEditing(false);
    if (draft !== (value || '')) onCommit?.(draft);
  };

  if (!editing) {
    return (
      <Tag
        role="button"
        tabIndex={0}
        onClick={startEditing}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            startEditing();
          }
        }}
        title="Click to edit"
        className={cn(
          className,
          'cursor-text rounded-sm -mx-0.5 px-0.5 transition-colors',
          'hover:bg-[hsl(var(--primary))]/10 hover:ring-1 hover:ring-[hsl(var(--primary))]/30',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[hsl(var(--primary))]/40',
          !value && 'italic text-muted-foreground/60 print:hidden'
        )}
        data-testid="editable-text"
      >
        {value || placeholder}
      </Tag>
    );
  }

  const fieldClass = cn(
    'w-full rounded-sm border border-[hsl(var(--primary))]/40 bg-[hsl(var(--muted))]/40',
    'px-1.5 py-1 outline-none focus:border-[hsl(var(--primary))]/70',
    multiline ? 'resize-none' : ''
  );
  // font: inherit keeps the field looking like the text it replaced.
  const fieldStyle = { font: 'inherit', color: 'inherit', lineHeight: 'inherit' };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setDraft(value || '');
      setEditing(false);
      return;
    }
    if (e.key === 'Enter' && (!multiline || e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      commit();
    }
  };

  return multiline ? (
    <textarea
      ref={fieldRef}
      value={draft}
      rows={1}
      onChange={(e) => {
        setDraft(e.target.value);
        e.target.style.height = 'auto';
        e.target.style.height = `${e.target.scrollHeight}px`;
      }}
      onBlur={commit}
      onKeyDown={onKeyDown}
      className={fieldClass}
      style={fieldStyle}
      data-testid="editable-text-field"
    />
  ) : (
    <input
      ref={fieldRef}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={onKeyDown}
      className={fieldClass}
      style={fieldStyle}
      data-testid="editable-text-field"
    />
  );
};

export default EditableText;
